import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar,
  Clock,
  MapPin,
  Users,
  DollarSign,
  Plus,
  Edit2,
  Trash2,
  ChevronRight,
  Music,
  Sun,
  Moon,
  AlertCircle,
  Ticket,
  Eye,
  Copy,
  Download,
  Upload
} from 'lucide-react';
import { api } from '../utils/api';
import EventModal from '../components/EventModal';
import Modal from '../components/Modal';
import { exportToCSV } from '../utils/export';

const EventsPage = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [importingEvents, setImportingEvents] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.events.getAll();
      setEvents(response.events);
    } catch (error) {
      console.error('Error loading events:', error);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (eventData) => {
    try {
      const response = await api.events.create(eventData);
      setEvents([...events, response.event]);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding event:', error);
      throw error;
    }
  };

  const handleEditEvent = async (eventData) => {
    try {
      const response = await api.events.update(editingEvent.id, eventData);
      setEvents(events.map(e => e.id === editingEvent.id ? response.event : e));
      setEditingEvent(null);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  };

  const handleDeleteEvent = async () => {
    try {
      await api.events.delete(deletingEvent.id);
      setEvents(events.filter(e => e.id !== deletingEvent.id));
      setDeletingEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete event');
    }
  };

  const handleDuplicateEvent = (event) => {
    const duplicatedEvent = {
      ...event,
      name: `${event.name} (Copy)`,
      date: '',
      status: 'planning'
    };
    delete duplicatedEvent.id;
    delete duplicatedEvent.createdAt;
    delete duplicatedEvent.updatedAt;
    setEditingEvent(duplicatedEvent);
    setShowAddModal(true);
  };

  const getEventIcon = (type) => {
    switch(type) {
      case 'breakfast': return <Sun className="text-[#D4A574]" size={20} />;
      case 'sunset': return <Music className="text-orange-500" size={20} />;
      case 'night': return <Moon className="text-blue-500" size={20} />;
      case 'festival': return <Calendar className="text-purple-500" size={20} />;
      case 'special': return <Ticket className="text-pink-500" size={20} />;
      default: return <Calendar className="text-[#888888]" size={20} />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      concept: 'bg-[#888888]/20 text-[#888888]',
      planning: 'bg-[#2D6B42]/20 text-[#2D6B42]',
      confirmed: 'bg-green-500/20 text-green-500',
      onsale: 'bg-blue-500/20 text-blue-500',
      soldout: 'bg-[#D4A574]/20 text-[#D4A574]',
      completed: 'bg-purple-500/20 text-purple-500',
      cancelled: 'bg-red-500/20 text-red-500'
    };
    return colors[status] || colors.concept;
  };

  const filterEvents = (status) => {
    const now = new Date();
    return events.filter(event => {
      if (status === 'upcoming') {
        return event.status !== 'completed' && event.status !== 'cancelled' && event.status !== 'concept';
      } else if (status === 'past') {
        return event.status === 'completed' || (event.date && new Date(event.date) < now);
      } else if (status === 'concepts') {
        return event.status === 'concept';
      }
      return false;
    });
  };

  const handleExportEvents = () => {
    const exportData = filterEvents(activeTab).map(event => ({
      Name: event.name,
      Type: event.type || '',
      Date: event.date ? new Date(event.date).toLocaleDateString() : '',
      Time: event.time || '',
      Venue: event.venue || '',
      Status: event.status,
      Capacity: event.capacity || '',
      'Tickets Sold': event.ticketsSold || 0,
      'Early Bird Price': event.earlyBirdPrice || '',
      'Regular Price': event.regularPrice || '',
      'Door Price': event.doorPrice || '',
      Description: event.description || '',
      Tags: event.tags?.join('; ') || '',
      Lineup: event.lineup?.join('; ') || ''
    }));
    
    exportToCSV(exportData, `events-${activeTab}.csv`);
  };

  const handleImportEvents = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportingEvents(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const rows = text.split('\n');
          const headers = rows[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, ''));
          
          let importCount = 0;
          
          for (let i = 1; i < rows.length; i++) {
            if (!rows[i].trim()) continue;
            
            const values = rows[i].split(',').map(v => v.trim());
            const eventData = {};
            
            headers.forEach((header, index) => {
              const value = values[index] || '';
              switch(header) {
                case 'tags':
                case 'lineup':
                  eventData[header] = value ? value.split(';').map(t => t.trim()) : [];
                  break;
                case 'capacity':
                case 'ticketssold':
                  eventData[header === 'ticketssold' ? 'ticketsSold' : header] = parseInt(value) || 0;
                  break;
                case 'earlybirdprice':
                case 'regularprice':
                case 'doorprice':
                  const fieldName = header.replace('price', 'Price').replace('earlybird', 'earlyBird');
                  eventData[fieldName] = parseFloat(value) || 0;
                  break;
                default:
                  eventData[header] = value;
              }
            });
            
            if (eventData.name) {
              try {
                await api.events.create(eventData);
                importCount++;
              } catch (error) {
                console.error(`Failed to import event ${eventData.name}:`, error);
              }
            }
          }
          
          await loadEvents();
          alert(`Successfully imported ${importCount} events`);
        } catch (error) {
          console.error('Error parsing CSV:', error);
          setError('Failed to parse CSV file');
        }
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Error importing events:', error);
      setError('Failed to import events');
    } finally {
      setImportingEvents(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const upcomingEvents = filterEvents('upcoming');
  const pastEvents = filterEvents('past');
  const conceptEvents = filterEvents('concepts');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2">Events</h1>
          <p className="text-[#888888]">Manage your events and concepts</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary flex items-center gap-2"
            disabled={importingEvents}
          >
            <Upload size={20} />
            {importingEvents ? 'Importing...' : 'Import'}
          </button>
          <button 
            onClick={handleExportEvents}
            className="btn-secondary flex items-center gap-2"
          >
            <Download size={20} />
            Export
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Create Event
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-[#333333]">
        <div className="flex gap-8">
          {[
            { id: 'upcoming', label: 'Upcoming', count: upcomingEvents.length },
            { id: 'past', label: 'Past', count: pastEvents.length },
            { id: 'concepts', label: 'Concepts', count: conceptEvents.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 transition-all duration-300 ${
                activeTab === tab.id
                  ? 'text-[#D4A574] border-b-2 border-[#D4A574]'
                  : 'text-[#888888] hover:text-white'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {activeTab === 'upcoming' && upcomingEvents.map((event) => (
          <div key={event.id} className="card-dark hover:border-[#2D6B42] transition-all duration-300">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-[#0A0A0A] rounded-lg">
                  {getEventIcon(event.type)}
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-1">{event.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(event.status)}`}>
                    {event.status}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setViewingEvent(event)}
                  className="p-2 hover:bg-[#0A0A0A] rounded-lg transition-colors"
                  title="View Details"
                >
                  <Eye size={16} />
                </button>
                <button 
                  onClick={() => setEditingEvent(event)}
                  className="p-2 hover:bg-[#0A0A0A] rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  onClick={() => handleDuplicateEvent(event)}
                  className="p-2 hover:bg-[#0A0A0A] rounded-lg transition-colors"
                  title="Duplicate"
                >
                  <Copy size={16} />
                </button>
                <button 
                  onClick={() => setDeletingEvent(event)}
                  className="p-2 hover:bg-[#0A0A0A] rounded-lg transition-colors text-red-500"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <p className="text-sm text-[#888888] mb-4">{event.description}</p>

            <div className="space-y-3">
              {event.date && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar size={16} className="text-[#888888]" />
                  <span>{new Date(event.date).toLocaleDateString('en-GB', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
              )}
              {event.time && (
                <div className="flex items-center gap-3 text-sm">
                  <Clock size={16} className="text-[#888888]" />
                  <span>{event.time}</span>
                </div>
              )}
              {event.venue && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin size={16} className="text-[#888888]" />
                  <span>{event.venue}</span>
                </div>
              )}
              {event.capacity && (
                <div className="flex items-center gap-3 text-sm">
                  <Users size={16} className="text-[#888888]" />
                  <span>Capacity: {event.capacity}</span>
                </div>
              )}
              {event.ticketPrice && (
                <div className="flex items-center gap-3 text-sm">
                  <DollarSign size={16} className="text-[#888888]" />
                  <span>{event.ticketPrice}</span>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-[#333333]">
              <button 
                onClick={() => setViewingEvent(event)}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                View Details
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        ))}

        {activeTab === 'past' && pastEvents.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Calendar className="mx-auto text-[#333333] mb-4" size={48} />
            <p className="text-[#888888]">No past events yet</p>
          </div>
        )}

        {activeTab === 'past' && pastEvents.map((event) => (
          <div key={event.id} className="card-dark opacity-75">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-[#0A0A0A] rounded-lg">
                {getEventIcon(event.type)}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-medium mb-1">{event.name}</h3>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(event.status)}`}>
                  {event.status}
                </span>
              </div>
            </div>
            <p className="text-sm text-[#888888] mb-4">{event.description}</p>
            {event.date && (
              <p className="text-sm text-[#888888]">
                {new Date(event.date).toLocaleDateString('en-GB', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </div>
        ))}

        {activeTab === 'concepts' && conceptEvents.map((concept) => (
          <div key={concept.id} className="card-dark">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-[#0A0A0A] rounded-lg">
                {getEventIcon(concept.type)}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-medium mb-1">{concept.name}</h3>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(concept.status)}`}>
                  {concept.status}
                </span>
              </div>
            </div>
            <p className="text-sm text-[#888888] mb-4">{concept.description}</p>
            <div className="flex gap-2">
              <button 
                onClick={() => {
                  setEditingEvent({ ...concept, status: 'planning' });
                }}
                className="btn-secondary text-sm"
              >
                Develop Concept
              </button>
              <button 
                onClick={() => setDeletingEvent(concept)}
                className="btn-secondary text-sm text-red-500 hover:bg-red-500/10"
              >
                Delete
              </button>
            </div>
          </div>
        ))}

        {activeTab === 'concepts' && conceptEvents.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Music className="mx-auto text-[#333333] mb-4" size={48} />
            <p className="text-[#888888]">No event concepts yet</p>
            <button 
              onClick={() => {
                setEditingEvent({ status: 'concept' });
                setShowAddModal(true);
              }}
              className="btn-secondary mt-4"
            >
              Create Concept
            </button>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="card-dark">
        <h2 className="text-xl font-light mb-4">Event Analytics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-light">{upcomingEvents.length}</p>
            <p className="text-sm text-[#888888]">Upcoming</p>
          </div>
          <div>
            <p className="text-2xl font-light">
              {upcomingEvents.filter(e => {
                const eventDate = new Date(e.date);
                const now = new Date();
                return eventDate.getMonth() === now.getMonth() && 
                       eventDate.getFullYear() === now.getFullYear();
              }).length}
            </p>
            <p className="text-sm text-[#888888]">This Month</p>
          </div>
          <div>
            <p className="text-2xl font-light">{conceptEvents.length}</p>
            <p className="text-sm text-[#888888]">Concepts</p>
          </div>
          <div>
            <p className="text-2xl font-light">
              €{events.filter(e => e.status === 'completed').reduce((sum, e) => {
                // Extract revenue from event (this would come from actual sales data)
                return sum + 0;
              }, 0)}
            </p>
            <p className="text-sm text-[#888888]">Revenue YTD</p>
          </div>
        </div>
      </div>

      {/* Add/Edit Event Modal */}
      <EventModal
        isOpen={showAddModal || !!editingEvent}
        onClose={() => {
          setShowAddModal(false);
          setEditingEvent(null);
        }}
        event={editingEvent}
        onSave={editingEvent?.id ? handleEditEvent : handleAddEvent}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingEvent}
        onClose={() => setDeletingEvent(null)}
        title="Delete Event"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete <strong>{deletingEvent?.name}</strong>?</p>
          <p className="text-sm text-[#888888]">This action cannot be undone.</p>
          
          <div className="flex gap-3 justify-end pt-4">
            <button
              onClick={() => setDeletingEvent(null)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteEvent}
              className="btn-primary bg-red-500 hover:bg-red-600"
            >
              Delete Event
            </button>
          </div>
        </div>
      </Modal>

      {/* View Event Details Modal */}
      <Modal
        isOpen={!!viewingEvent}
        onClose={() => setViewingEvent(null)}
        title="Event Details"
        maxWidth="max-w-2xl"
      >
        {viewingEvent && (
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#0A0A0A] rounded-lg">
                {getEventIcon(viewingEvent.type)}
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-light mb-2">{viewingEvent.name}</h3>
                <span className={`text-sm px-3 py-1 rounded-full ${getStatusBadge(viewingEvent.status)}`}>
                  {viewingEvent.status}
                </span>
              </div>
            </div>

            {viewingEvent.description && (
              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-2">Description</h4>
                <p className="text-sm">{viewingEvent.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Event Details</h4>
                <div className="space-y-3">
                  {viewingEvent.date && (
                    <div className="flex items-center gap-3 text-sm">
                      <Calendar size={16} className="text-[#888888]" />
                      <span>{new Date(viewingEvent.date).toLocaleDateString('en-GB', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                  )}
                  {viewingEvent.time && (
                    <div className="flex items-center gap-3 text-sm">
                      <Clock size={16} className="text-[#888888]" />
                      <span>{viewingEvent.time}</span>
                    </div>
                  )}
                  {viewingEvent.venue && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin size={16} className="text-[#888888]" />
                      <span>{viewingEvent.venue}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Capacity & Pricing</h4>
                <div className="space-y-3">
                  {viewingEvent.capacity && (
                    <div className="flex items-center gap-3 text-sm">
                      <Users size={16} className="text-[#888888]" />
                      <span>Capacity: {viewingEvent.capacity}</span>
                    </div>
                  )}
                  {viewingEvent.ticketPrice && (
                    <div className="flex items-center gap-3 text-sm">
                      <DollarSign size={16} className="text-[#888888]" />
                      <span>{viewingEvent.ticketPrice}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {viewingEvent.lineup && viewingEvent.lineup.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Lineup</h4>
                <div className="space-y-2">
                  {viewingEvent.lineup.map((artist, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <Music size={16} className="text-[#888888]" />
                      <span>{artist}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {viewingEvent.tags && viewingEvent.tags.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[#888888] mb-3">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {viewingEvent.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-[#0A0A0A] rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-[#333333]">
              <button
                onClick={() => {
                  setViewingEvent(null);
                  setEditingEvent(viewingEvent);
                }}
                className="btn-secondary flex items-center gap-2"
              >
                <Edit2 size={16} />
                Edit Event
              </button>
              <button
                onClick={() => setViewingEvent(null)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleImportEvents}
        className="hidden"
      />
    </div>
  );
};

export default EventsPage;